import "rxjs/add/operator/map";
import { Observable } from "rxjs";
import {
    SHIPPING_CARRIER_SERVICES_COMPLETE,
    SHIPPING_PROMOTION_COMPLETE,
    SHIPPING_PROMOTION_DELETE,
    SHIPPING_PROMOTION_DELETE_COMPLETE,
    SHIPPING_PROMOTION_REQUEST,
    SHIPPING_PROMOTIONS_COMPLETE,
    SHIPPING_PROMOTIONS_REQUEST,
    SHIPPING_PROMOTIONS_UPDATE,
} from "constants/action-types";
import siteConfig from "site-config";
import { formatMessage } from "lib/globalize-util";
import { ajax } from "lib/observable";
import * as qs from "qs";
import { add as snackbarAdd } from "actions/snackbars";
import { getRecordsFromSchema, normalizeData } from "lib/api";
import { ShippingPromotions as ShippingPromotionsSchema } from "schemas/shipping-promotions";
import { CarrierServiceList as CarrierServiceListsSchema } from "schemas/shipping-account";
import { updateEntities } from "redux-query";
import { APIError } from "lib/errors";

export const fetchShippingPromotions = (action$, store) => {
    return action$
        .ofType(SHIPPING_PROMOTIONS_REQUEST)
        .map((action) => {
            return action.payload;
        })
        .concatMap((options) => {
            const url = options.url ? options.url : `/api/v3.0/shipping/promotions?${qs.stringify(options)}`;
            return ajax({
                method: "GET",
                url: `${siteConfig.api}${url}`,
            })
                .map(({ response, status, xhr }) => {
                    if (response && status === 200) {
                        const nextPage = xhr.getResponseHeader("Next");
                        const promotionRecords = getRecordsFromSchema(ShippingPromotionsSchema);
                        const promotionData = normalizeData(response, ShippingPromotionsSchema, promotionRecords);
                        return {
                            entities: promotionData.entities,
                            result: {
                                ids: promotionData.result,
                                nextPage: nextPage ? nextPage : null,
                            },
                            error: false,
                        };
                    } else {
                        return { error: true, entities: null, result: [] };
                    }
                })
                .flatMap(({ entities, result, error }) => {
                    if (error) {
                        return [
                            store.dispatch(snackbarAdd({ message: "Error fetching promotions" })),
                            {
                                payload: [],
                                error: true,
                                type: SHIPPING_PROMOTIONS_COMPLETE,
                            },
                        ];
                    } else {
                        return [
                            updateEntities({
                                shippingPromotion: (currentPromotions = {}) => {
                                    return {
                                        ...currentPromotions,
                                        ...entities.shippingPromotion,
                                    };
                                },
                            }),
                            {
                                payload: result,
                                type: SHIPPING_PROMOTIONS_COMPLETE,
                            },
                        ];
                    }
                })
                .catch((error) => {
                    let errorMessage = formatMessage("Error fetching promotions");
                    if (error.status === 401) {
                        errorMessage = formatMessage("You do not have permission to view promotions");
                    }
                    return Observable.merge(
                        Observable.of({
                            error: true,
                            payload: new APIError(errorMessage, error.xhr.status, error.xhr.statusText),
                            type: SHIPPING_PROMOTIONS_COMPLETE,
                        }),
                        Observable.of(snackbarAdd({ message: errorMessage }))
                    );
                });
        });
};

export const deleteShippingPromotions = (action$, store) => {
    return action$
        .ofType(SHIPPING_PROMOTION_DELETE)
        .map((action) => {
            return action.payload;
        })
        .concatMap((id) => {
            return ajax({
                method: "DELETE",
                url: `${siteConfig.api}/api/v3.0/shipping/promotion/${id}`,
            })
                .map(({ response, status }) => {
                    if (response && [202, 201].includes(status)) {
                        return { entities: { shippingPromotion: {} }, result: id, error: false };
                    } else {
                        return { error: true, entities: null, result: [] };
                    }
                })
                .flatMap(({ entities, result, error }) => {
                    if (error) {
                        return [
                            store.dispatch(snackbarAdd({ message: "Error fetching promotions" })),
                            {
                                payload: [],
                                error: true,
                                type: SHIPPING_PROMOTION_DELETE_COMPLETE,
                            },
                        ];
                    } else {
                        return [
                            updateEntities({
                                shippingPromotion: (shippingPromotion = {}) => {
                                    delete shippingPromotion[id];
                                    return {
                                        ...shippingPromotion,
                                    };
                                },
                            }),
                            {
                                payload: result,
                                type: SHIPPING_PROMOTION_DELETE_COMPLETE,
                            },
                            snackbarAdd({ message: formatMessage("Promotion deleted") }),
                        ];
                    }
                })
                .catch((error) => {
                    let errorMessage = formatMessage("Error deleting promotions");
                    if (error.status === 401) {
                        errorMessage = formatMessage("You do not have permission to delete promotions");
                    }
                    return Observable.merge(
                        Observable.of({
                            error: true,
                            payload: new APIError(errorMessage, error.xhr.status, error.xhr.statusText),
                            type: SHIPPING_PROMOTION_DELETE_COMPLETE,
                        }),
                        Observable.of(snackbarAdd({ message: errorMessage }))
                    );
                });
        });
};

export const createOrEditPromotionEpic = (action$, store) => {
    return action$
        .ofType(SHIPPING_PROMOTIONS_UPDATE)
        .map((action) => {
            const promotion = action.payload;
            promotion.apply_before_split = promotion.applyBeforeSplit;
            promotion.hide_from_buyers = promotion.hideFromBuyers;
            promotion.service_code = promotion.serviceCode;

            return promotion;
        })
        .concatMap((promotion) => {
            return ajax({
                method: promotion.id ? "PUT" : "POST",
                body: promotion,
                url: promotion.id
                    ? `${siteConfig.api}/api/v3.0/shipping/promotion/${promotion.id}`
                    : `${siteConfig.api}/api/v3.0/shipping/promotions`,
            })
                .map(({ response, status }) => {
                    if (response && [202, 201].includes(status)) {
                        response.carrier_id = promotion.carrierId;
                        const promotionRecords = getRecordsFromSchema(ShippingPromotionsSchema);
                        const promotionData = normalizeData([response], ShippingPromotionsSchema, promotionRecords);
                        return { entities: promotionData.entities, result: promotionData.result, error: false };
                    } else {
                        return { error: true, entities: null, result: [] };
                    }
                })
                .flatMap(({ entities, result, error }) => {
                    if (error) {
                        return [
                            store.dispatch(snackbarAdd({ message: "Error saving promotion" })),
                            {
                                payload: [],
                                error: true,
                                type: SHIPPING_PROMOTION_COMPLETE,
                            },
                        ];
                    } else {
                        return [
                            updateEntities({
                                shippingPromotion: (shippingPromotion = {}) => {
                                    return {
                                        ...entities.shippingPromotion,
                                    };
                                },
                            }),
                            {
                                payload: result,
                                type: SHIPPING_PROMOTION_COMPLETE,
                            },
                            snackbarAdd({
                                message: `${
                                    promotion.id
                                        ? formatMessage("Promotion updated")
                                        : formatMessage("Promotion created")
                                }`,
                            }),
                        ];
                    }
                })
                .catch((error) => {
                    let errorMessage = formatMessage("Error saving promotion");
                    if (error.status === 401) {
                        errorMessage = formatMessage("You do not have permission to edit promotions");
                    }
                    return Observable.merge(
                        Observable.of({
                            error: true,
                            payload: new APIError(errorMessage, error.xhr.status, error.xhr.statusText),
                            type: SHIPPING_PROMOTION_COMPLETE,
                        }),
                        Observable.of(snackbarAdd({ message: errorMessage }))
                    );
                });
        });
};

export const fetchShippingPromotion = (action$, store) => {
    return action$
        .ofType(SHIPPING_PROMOTION_REQUEST)
        .map((action) => {
            return action.payload;
        })
        .concatMap((id) => {
            return ajax({
                method: "GET",
                url: `${siteConfig.api}/api/v3.0/shipping/promotion/${id}`,
            })
                .map(({ response, status }) => {
                    if (response && status === 200) {
                        const promotion = response.promotions[0];
                        const serviceCode = promotion.service_code;
                        let carrierServiceData;
                        response.carriers.some((carrier) => {
                            const isMatchingCarrier = (carrier.services || []).some((service) => {
                                return service.service_code === serviceCode;
                            });

                            if (isMatchingCarrier) {
                                promotion.carrier_id = carrier.id;
                                promotion.external_id = carrier.external_id;
                                carrierServiceData = carrier.services;
                                return true;
                            } else {
                                return false;
                            }
                        });

                        let serviceRecords;
                        let serviceData = { entities: {}, result: [] };
                        if (carrierServiceData) {
                            // Possible call to get services for a carrier fails on Ship Engine's side
                            serviceRecords = getRecordsFromSchema(CarrierServiceListsSchema);
                            serviceData = normalizeData(carrierServiceData, CarrierServiceListsSchema, serviceRecords);
                        }

                        const promotionRecords = getRecordsFromSchema(ShippingPromotionsSchema);
                        const promotionData = normalizeData(
                            response.promotions,
                            ShippingPromotionsSchema,
                            promotionRecords
                        );
                        return {
                            entities: {
                                ...promotionData.entities,
                                ...serviceData.entities,
                            },
                            error: false,
                            result: {
                                promotionResult: promotionData.result,
                                serviceResult: [serviceData.result],
                            },
                        };
                    } else {
                        return { error: true, entities: null, result: [] };
                    }
                })
                .flatMap(({ entities, result, error }) => {
                    if (error) {
                        return [
                            store.dispatch(snackbarAdd({ message: "Error fetching promotions" })),
                            {
                                payload: [],
                                error: true,
                                type: SHIPPING_PROMOTION_COMPLETE,
                            },
                        ];
                    } else {
                        return [
                            updateEntities({
                                shippingPromotion: (shippingPromotion = {}) => {
                                    return {
                                        ...entities.shippingPromotion,
                                    };
                                },
                                carrierService: (carrierService = {}) => {
                                    return {
                                        ...entities.carrierServiceList,
                                    };
                                },
                            }),
                            {
                                payload: (result as any).promotionResult,
                                type: SHIPPING_PROMOTION_COMPLETE,
                            },
                            {
                                payload: (result as any).serviceResult,
                                type: SHIPPING_CARRIER_SERVICES_COMPLETE,
                            },
                        ];
                    }
                })
                .catch((error) => {
                    let errorMessage = formatMessage("Error fetching promotions");
                    if (error.status === 401) {
                        errorMessage = formatMessage("You do not have permission to view promotions");
                    } else if (error.status === 404) {
                        errorMessage = formatMessage("Promotion not found");
                    }
                    return Observable.merge(
                        Observable.of({
                            error: true,
                            payload: new APIError(errorMessage, error.xhr.status, error.xhr.statusText),
                            type: SHIPPING_PROMOTION_COMPLETE,
                        }),
                        Observable.of(snackbarAdd({ message: errorMessage }))
                    );
                });
        });
};
